/**
 * Category description may be a JSON string (often in `translated.description`):
 *   { "title": "...", "text": "...", "text-prices": "<html>..." }
 */
export function readCategoryDescriptionRaw(category) {
    if (!category || typeof category !== 'object') return '';
    const candidates = [
        category.translated?.description,
        category.description,
        category.value,
        category.translated?.value,
    ];
    for (const c of candidates) {
        const s = String(c ?? '').trim();
        if (s) return s;
    }
    return '';
}

/** Strip CMS HTML wrapper and isolate `{...}` before JSON.parse. */
function extractJsonObjectString(raw) {
    let s = String(raw ?? '').trim();
    if (!s) return '';

    s = s
        .replace(/&quot;/gi, '"')
        .replace(/&#34;/g, '"')
        .replace(/&lt;/gi, '<')
        .replace(/&gt;/gi, '>')
        .replace(/&amp;/gi, '&')
        .replace(/<[^>]*>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();

    const start = s.indexOf('{');
    const end = s.lastIndexOf('}');
    if (start !== -1 && end > start) {
        return s.slice(start, end + 1);
    }
    return s;
}

/** CMS `html` / `text-prices` blobs often break strict JSON — strip before parse. */
const HTML_FIELD_RE = /,?\s*["']html["']\s*:\s*(?:'[\s\S]*?'|"[\s\S]*?")/i;
const TEXT_PRICES_FIELD_RE = /,?\s*["']text-prices["']\s*:\s*(?:'[\s\S]*?'|"[\s\S]*?")/i;

function unescapeJsonString(value) {
    return String(value ?? '')
        .replace(/\\"/g, '"')
        .replace(/\\\\/g, '\\');
}

/** Parse category description JSON; tolerate single-quoted `text-prices`. */
function parseCategoryJsonObject(jsonStr) {
    if (!jsonStr) return null;

    const attempts = [
        jsonStr,
        jsonStr.replace(HTML_FIELD_RE, '').replace(TEXT_PRICES_FIELD_RE, '').replace(/,\s*}/g, '}'),
    ];

    for (const candidate of attempts) {
        try {
            const parsed = JSON.parse(candidate);
            if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
                return parsed;
            }
        } catch (_) {
            /* try next */
        }
    }

    const titleMatch = jsonStr.match(/"title"\s*:\s*"((?:\\.|[^"\\])*)"/);
    const textMatch = jsonStr.match(/"text"\s*:\s*"((?:\\.|[^"\\])*)"/);
    if (!titleMatch && !textMatch) return null;

    return {
        ...(titleMatch ? { title: unescapeJsonString(titleMatch[1]) } : {}),
        ...(textMatch ? { text: unescapeJsonString(textMatch[1]) } : {}),
    };
}

export function parseCategoryDescriptionPayload(category) {
    const fallbackTitle = String(category?.translated?.title ?? category?.title ?? 'Category').trim();
    const raw = readCategoryDescriptionRaw(category);

    if (!raw) {
        return { title: fallbackTitle, text: '', textPrices: '' };
    }

    const jsonStr = extractJsonObjectString(raw);
    const parsed = parseCategoryJsonObject(jsonStr);

    if (parsed) {
        return {
            title: String(parsed.title ?? fallbackTitle).trim() || fallbackTitle,
            text: String(parsed.text ?? '').trim(),
            textPrices: String(parsed['text-prices'] ?? parsed.textPrices ?? '').trim(),
        };
    }

    return { title: fallbackTitle, text: '', textPrices: '' };
}

/**
 * Product description JSON (hero pricing cards):
 *   { "title": "...", "html": "<p>...</p>", "text": "..." }
 */
export function readProductDescriptionRaw(product) {
    if (!product || typeof product !== 'object') return '';
    const candidates = [
        product.translated?.description,
        product.description,
    ];
    for (const c of candidates) {
        const s = String(c ?? '').trim();
        if (s) return s;
    }
    return '';
}

function decodeCmsJsonEntities(raw) {
    return String(raw ?? '')
        .replace(/&quot;/gi, '"')
        .replace(/&#34;/g, '"')
        .replace(/&lt;/gi, '<')
        .replace(/&gt;/gi, '>')
        .replace(/&amp;/gi, '&')
        .trim();
}

function stripOuterHtmlWrapper(raw) {
    let s = String(raw ?? '').trim();
    if (!s.includes('{')) return s;
    return s
        .replace(/^<p[^>]*>\s*/i, '')
        .replace(/\s*<\/p>\s*$/i, '')
        .trim();
}

function parseProductDescriptionJson(raw) {
    const empty = { title: '', html: '', text: '', hasJsonTitle: false };
    if (!raw) return empty;

    const candidates = [
        stripOuterHtmlWrapper(decodeCmsJsonEntities(raw)),
        decodeCmsJsonEntities(raw),
        String(raw).trim(),
    ];

    for (const candidate of candidates) {
        const trimmed = candidate.trim();
        if (!trimmed.startsWith('{')) continue;

        try {
            const parsed = JSON.parse(trimmed);
            if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) continue;

            const title = String(parsed.title ?? '').trim();
            if (!title) continue;

            return {
                title,
                html: String(parsed.html ?? '').trim(),
                text: String(parsed.text ?? '').trim(),
                hasJsonTitle: true,
            };
        } catch (_) {
            /* try next candidate */
        }
    }

    return empty;
}

export function parseProductDescriptionPayload(product) {
    return parseProductDescriptionJson(readProductDescriptionRaw(product));
}

/** Buy-flow product cards: JSON `html` when present, else legacy description HTML/text. */
export function resolveProductCardDescription(product) {
    const parsed = parseProductDescriptionPayload(product);
    const apiTitle = String(product?.translated?.title ?? product?.name ?? '').trim();
    const raw = readProductDescriptionRaw(product);

    if (parsed.hasJsonTitle) {
        return {
            displayTitle: parsed.title || apiTitle,
            html: parsed.html,
        };
    }

    return {
        displayTitle: apiTitle,
        html: raw,
    };
}
